import * as CL from '@design-system/component-library';
import { dsClass } from '../../common/constants/dsClasses';
import {
  activationFeeMsg,
  eurosPerMonthCapMsg,
  pcsMsg,
  t,
  totalWithCurlyBracketsMsg,
  vatPercentageMsg,
} from '../../common/i18n';
import { formatSum } from '../../common/utils/priceUtils';
import type { PriceData } from './types';
import type { Price } from '../../generated/api/price';
import type { JSX } from 'react';

interface BadgeConfig {
  text: string;
  subtle?: boolean;
}

interface DisclaimerFieldsProps {
  oneTimePaymentText: string;
  priceData?: PriceData;
  listPrice?: Price;
  badge?: BadgeConfig;
  isVoucherItem?: boolean;
  quantity?: number;
}

export const DisclaimerFields = ({
  oneTimePaymentText,
  priceData,
  listPrice,
  badge,
  isVoucherItem,
  quantity = 1,
}: DisclaimerFieldsProps) => {
  const hasOneTimeCharge = !!priceData?.price?.oneTimeCharge;
  const hasMonthlyCharges = !!priceData?.price?.monthlyRecurringCharge;
  const payments = priceData?.price?.payments || 0;
  const vat = t.A0OJ(vatPercentageMsg, '0');

  const getFields = (): JSX.Element[] => {
    const totalPrice = formatSum(Number(priceData?.price?.monthlyRecurringCharge) * payments * quantity) || '';
    const totalPriceText = t.W1RX(totalWithCurlyBracketsMsg, totalPrice);
    const agreementText = t.CF93('{} month agreement', String(payments));

    const getNormalPrice = () => {
      if (hasOneTimeCharge) {
        return `(norm. ${formatSum(Number(listPrice?.oneTimeCharge) * quantity) || ''})`;
      }
      if (payments === 0) {
        return '';
      }
      return `(norm. ${formatSum(Number(listPrice?.monthlyRecurringCharge) * payments * quantity) || ''})`;
    };

    if (isVoucherItem) {
      const getVoucherPrice = () => {
        if (hasOneTimeCharge) {
          return formatSum(Number(priceData?.price?.oneTimeCharge) * quantity) || '';
        }
        if (payments === 0) {
          return '';
        }
        return totalPriceText;
      };

      const price = getVoucherPrice();
      const normalPrice = getNormalPrice();

      if (hasMonthlyCharges && hasOneTimeCharge) {
        const activationFee = formatSum(Number(listPrice?.oneTimeCharge));
        return [
          <div key="normalPrice" className="voucherNormalPrice">
            {`(norm. ${formatSum(Number(listPrice?.monthlyRecurringCharge) * quantity, true) || ''} ${t.H37G(eurosPerMonthCapMsg)})`}
          </div>,
          activationFee && (
            <div key="activationFee">{`${t.HWDR(activationFeeMsg)} ${activationFee}${quantity > 1 ? `/${t.B3MG(pcsMsg)}` : ''}`}</div>
          ),
          <div key="vat">{vat}</div>,
        ].filter(Boolean) as JSX.Element[];
      }

      if (hasMonthlyCharges) {
        return [
          <div key="price">{price}</div>,
          <div key="normalPrice" className="voucherNormalPrice">
            {normalPrice}
          </div>,
          <div key="vat">{vat}</div>,
        ];
      }
      if (hasOneTimeCharge) {
        return [
          <div key="oneTime">{t.ASEI('One-time payment')}</div>,
          <div key="normalPrice" className="voucherNormalPrice">
            {normalPrice}
          </div>,
          <div key="vat">{vat}</div>,
        ];
      }
      return [<div key="oneTimePayment">{oneTimePaymentText}</div>, <div key="vat">{vat}</div>];
    }

    if (hasOneTimeCharge) {
      return [<div key="oneTimePayment">{oneTimePaymentText}</div>, <div key="vat">{vat}</div>];
    }
    if (hasMonthlyCharges && payments > 0) {
      return [
        <div key="agreement">{agreementText}</div>,
        <div key="total">{totalPriceText}</div>,
        <div key="vat">{vat}</div>,
      ];
    }
    if (priceData?.price?.oneTimeCharge === 0 && priceData.price.monthlyRecurringCharge === 0) {
      // For 0€ sim-card
      return [<div key="oneTimePayment">{oneTimePaymentText}</div>, <div key="vat">{vat}</div>];
    }
    return [<div key="vat">{vat}</div>];
  };

  return (
    <>
      {getFields()}
      {badge && (
        <div className={`${dsClass.PADDING_TOP_2} badge-wrapper`}>
          <CL.Badge text={badge.text} color="discount" subtle={badge.subtle} />
        </div>
      )}
    </>
  );
};
